import { Trans } from "@lingui/macro";
import { useCallback } from "react";
import { FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import { Address } from "viem";

import ChartBarIcon from "assets/icons/ChartBarIcon";
import CryptoBridgeIcon from "assets/icons/CryptoBridgeIcon";
import PoolIcon from "assets/icons/PoolIcon";
import SuitecaseIcon from "assets/icons/SuitecaseIcon";
import TradeIcon from "assets/icons/TradeIcon";
import { isHomeSite } from "lib/legacy";
import { useNotifyModalState } from "lib/useNotifyModalState";
import { userAnalytics } from "lib/userAnalytics";
import { ReferralTopMenuClickEvent } from "lib/userAnalytics/types";

import ExternalLink from "components/ExternalLink/ExternalLink";

import logoImg from "img/logo.svg";

import { HeaderLink } from "./HeaderLink";

import "./Header.scss";
import Button from "components/Button/Button";

import useWallet from "lib/wallets/useWallet";

import AppHeaderLinkItem from "./AppHeaderLinkItem";

import { buildAccountDashboardUrl } from "pages/AccountDashboard/buildAccountDashboardUrl";
import UserAddIcon from "assets/icons/UserAddIcon";
import TrophyIcon from "assets/icons/TrophyIcon";
import { EXTERNAL_LINKS } from "config/links";
import { SOCIALS_CONFIG } from "config/socials";
import { APP_NAME } from "config/ui";

type Props = {
  small?: boolean;
  clickCloseIcon?: () => void;
  openSettings?: () => void;
  showRedirectModal: (to: string) => void;
};

export function AppHeaderLinks({ small, openSettings, clickCloseIcon, showRedirectModal }: Props) {
  const { openNotifyModal } = useNotifyModalState();
  const isLeaderboardActive = useCallback(
    (match, location) => Boolean(match) || location.pathname.startsWith("/competitions"),
    []
  );

  const { account } = useWallet();

  return (
    <div className="App-header-links">
      {small && (
        <div className="App-header-links-header">
          <Link className="App-header-link-main" to="/">
            <img src={logoImg} alt={`${APP_NAME} Logo`} />
          </Link>
          <Button
            variant="tertiary"
            size="small"
            className="h-[24px] min-w-[70px] flex-shrink-0 !text-white-alpha-60"
            onClick={() => clickCloseIcon && clickCloseIcon()}
          >
            <Trans>Close</Trans>
          </Button>
        </div>
      )}
      <div className="mb-[24px] mt-[32px] flex flex-col gap-[8px]">
        <AppHeaderLinkItem showRedirectModal={showRedirectModal} to="/trade" icon={TradeIcon}>
          <Trans>Trade</Trans>
        </AppHeaderLinkItem>
        <AppHeaderLinkItem showRedirectModal={showRedirectModal} to="/pools" icon={PoolIcon}>
          <Trans>Pool</Trans>
        </AppHeaderLinkItem>
        {account && (
          <AppHeaderLinkItem
            showRedirectModal={showRedirectModal}
            to={buildAccountDashboardUrl(account as Address, undefined, 2)}
            icon={SuitecaseIcon}
          >
            <Trans>Portfolio</Trans>
          </AppHeaderLinkItem>
        )}
        <AppHeaderLinkItem showRedirectModal={showRedirectModal} to="/referrals" icon={UserAddIcon}>
          <Trans>Referrals</Trans>
        </AppHeaderLinkItem>
        <AppHeaderLinkItem
          showRedirectModal={showRedirectModal}
          to="/leaderboard"
          icon={TrophyIcon}
          isActive={isLeaderboardActive}
        >
          <Trans>Leaderboard</Trans>
        </AppHeaderLinkItem>
        <AppHeaderLinkItem showRedirectModal={showRedirectModal} to="/stats" icon={ChartBarIcon}>
          <Trans>Statistics</Trans>
        </AppHeaderLinkItem>
        <AppHeaderLinkItem showRedirectModal={showRedirectModal} to="/bridge" icon={CryptoBridgeIcon}>
          <Trans>Bridge</Trans>
        </AppHeaderLinkItem>
      </div>
      <div className="mt-auto flex flex-col gap-[18px]">
        <ExternalLink href={EXTERNAL_LINKS.DEFI_LAMA} className="App-header-link-external">
          <Trans>DefiLama</Trans>
        </ExternalLink>
        <ExternalLink href={EXTERNAL_LINKS.DOCS} className="App-header-link-external">
          <Trans>Docs</Trans>
        </ExternalLink>
        <ExternalLink href={EXTERNAL_LINKS.TERM} className="App-header-link-external">
          <Trans>Terms and Condition</Trans>
        </ExternalLink>
        <ExternalLink href={EXTERNAL_LINKS.PRIVACY} className="App-header-link-external">
          <Trans>Privacy Policy</Trans>
        </ExternalLink>
        <ExternalLink href={EXTERNAL_LINKS.SUPPORT} className="App-header-link-external">
          <Trans>Support</Trans>
        </ExternalLink>
      </div>

      <p className="leading-none mt-[42px] text-center text-[10px] font-medium text-white-alpha-40">
        <Trans>Built with ❤️ by Asset Chain Team</Trans>
      </p>

      <div className="mt-[60px] flex flex-col gap-[11px]">
        <div className="flex items-center gap-[11px]">
          {SOCIALS_CONFIG.map((social) => (
            <ExternalLink key={social.name} href={social.url} className="App-header-link-social">
              {social.icon({ className: "App-header-link-social__icon" })}
            </ExternalLink>
          ))}
        </div>
        {/* <p className="leading-none text-[13px] font-medium text-[#888888]">
          <Trans>Network Status</Trans>
        </p> */}
      </div>
    </div>
  );
}
